import { useCallback, useEffect, useReducer } from 'react';

import { getFileIdFromName, UploadedFile } from 'pkg/components/FileUploader/UploadedFile';
import { useFileRejection } from 'pkg/components/FileUploader/useFileRejection';
import { getDiffBetweenArrays } from 'pkg/utils/getDiffBetweenArrays';

export enum AlertState {
  DISABLED,
  SUBMIT,
  FILE_TYPE,
  CLOSE,
  TOO_BIG_FILES,
}

enum AlertAction {
  DISABLE_ALERT,
  SET_CLOSE_ALERT,
  SET_SUBMIT_ALERT,
  SET_FILE_TYPE_ALERT,
  SET_TOO_BIG_FILES_ALERT,
  OPEN_UPLOADER,
  CLOSE_UPLOADER,
}

interface UploaderState {
  alert: AlertState;
  isUploaderOpen: boolean;
  fileTypes?: string[];
}

const alertsReducer = (state: UploaderState, action: { type: AlertAction; payload?: string[] }): UploaderState => {
  switch (action.type) {
    case AlertAction.DISABLE_ALERT:
      return { ...state, alert: AlertState.DISABLED, fileTypes: undefined };
    case AlertAction.SET_CLOSE_ALERT:
      return { ...state, alert: AlertState.CLOSE, fileTypes: undefined };
    case AlertAction.SET_FILE_TYPE_ALERT:
      return { ...state, alert: AlertState.FILE_TYPE, fileTypes: action.payload as string[] };
    case AlertAction.SET_SUBMIT_ALERT:
      return { ...state, alert: AlertState.SUBMIT };
    case AlertAction.SET_TOO_BIG_FILES_ALERT:
      return { ...state, alert: AlertState.TOO_BIG_FILES };
    case AlertAction.OPEN_UPLOADER:
      return { ...state, isUploaderOpen: true };
    case AlertAction.CLOSE_UPLOADER:
      return { isUploaderOpen: false, alert: AlertState.DISABLED, fileTypes: undefined };
    default:
      return state;
  }
};

export const useFileUploaderAlerts = ({
  files,
  loading,
  revertFiles,
  submitFiles,
  values,
}: {
  values: string[];
  files: UploadedFile[];
  loading: boolean;
  revertFiles: () => void;
  submitFiles: () => void;
}) => {
  const onFileRejection = useFileRejection();
  const [uploaderState, dispatch] = useReducer<typeof alertsReducer>(alertsReducer, {
    alert: AlertState.DISABLED,
    isUploaderOpen: false,
  });

  const disableAlert = useCallback(() => dispatch({ type: AlertAction.DISABLE_ALERT }), [dispatch]);
  const closeUploaderAndRevertFiles = () => {
    revertFiles();
    dispatch({ type: AlertAction.CLOSE_UPLOADER });
  };

  useEffect(() => {
    if (!loading) {
      disableAlert();
    }
  }, [loading, disableAlert]);

  const handleCloseAlertAccept = () => {
    closeUploaderAndRevertFiles();
    disableAlert();
  };

  const handleCloseUploader = () => {
    const submittedFiles = values.map((fileName) => ({
      name: fileName,
      id: getFileIdFromName(fileName),
    }));

    if (
      getDiffBetweenArrays<{ id: string }>(files, submittedFiles).length !== 0 ||
      getDiffBetweenArrays<{ id: string }>(submittedFiles, files).length !== 0
    ) {
      dispatch({ type: AlertAction.SET_CLOSE_ALERT });
    } else {
      closeUploaderAndRevertFiles();
    }
  };

  const openTooBigFilesAlert = () => dispatch({ type: AlertAction.SET_TOO_BIG_FILES_ALERT });

  const setRejectedFiles = (rejectedExtensions: string[]) => {
    onFileRejection(rejectedExtensions);
    dispatch({ type: AlertAction.SET_FILE_TYPE_ALERT, payload: rejectedExtensions });
  };

  const handleSubmit = () => {
    if (loading) {
      dispatch({ type: AlertAction.SET_SUBMIT_ALERT });
    } else {
      submitFiles();
      dispatch({ type: AlertAction.CLOSE_UPLOADER });
    }
  };

  return {
    uploaderState,
    actions: {
      disableAlert,
      handleCloseWithPendingFiles: handleCloseAlertAccept,
      setRejectedFiles,
      handleSubmit,
      openTooBigFilesAlert,
      handleCloseUploader,
      openModal: () => dispatch({ type: AlertAction.OPEN_UPLOADER }),
    },
  };
};
